import { type InputType } from 'config'
import { accountTypeWithBorrower as _accountTypeWithBorrower } from 'config/user.type.constants'
import { Appraisal2StatusType } from 'pages/Loan/OrderAppraisals/types'
import {
  defaultChannelOptions,
  getLoanTypeMapForSelect,
  pullThroughRatioPartiesMap,
} from 'reducers/loanDetail.reducer'
import { USER_STYLE_LABELS } from 'utils/theme'
import timeZoneMap from 'utils/timezone'

export enum LeverageVisibleKeys {
  AIV_LTV = 'aivLtv',
  ARV_LTV = 'arvLtv',
  LTC = 'ltc',
  LTP = 'ltp',
  CLTV = 'cltv',
}

export const LeverageVisibleKeyOptions: Record<LeverageVisibleKeys, string> = {
  [LeverageVisibleKeys.AIV_LTV]: 'AIV-LTV',
  [LeverageVisibleKeys.ARV_LTV]: 'ARV-LTV',
  [LeverageVisibleKeys.LTC]: 'LTC',
  [LeverageVisibleKeys.LTP]: 'LTP',
  [LeverageVisibleKeys.CLTV]: 'CLTV',
}

export const defaultInputs = (): Record<string, InputType> => {
  return {
    loanSection: {
      inputType: 'section',
      title: 'Loan',
      span: 'full',
    },
    loanNumberPrefix: {
      inputType: 'text',
      type: 'text',
      title: 'Loan Number Prefix',
      value: '',
      required: false,
    },
    minProposedLoanAmount: {
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Min Proposed Loan Amount',
      value: '',
      required: true,
    },
    maxProposedLoanAmount: {
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Max Proposed Loan Amount',
      value: '',
      required: true,
    },
    allowCentForLoanAmount: {
      inputType: 'check',
      title: 'Allow Cent for Loan Amount',
      value: false,
    },
    minAsIsValue: {
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Min As Is Appraised Value',
      value: '',
      required: true,
    },
    minAfterRepairValue: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Profitability Requirement',
      value: '',
      required: true,
    },
    minTotalConstructionFinanced: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Min Total Construction Financed',
      value: '',
      required: true,
    },
    maxTotalConstructionFinanced: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Max Total Construction Financed',
      value: '',
      required: true,
    },
    minConstructionFinancedHoldback: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Min Construction Financed Holdback',
      value: '',
      required: true,
    },
    maxConstructionFinancedHoldback: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Max Construction Financed Holdback',
      value: '',
      required: true,
    },
    maxPercentOfEstimatedClosingAmountToBorrower: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Max Percent of Estimated Closing Amount to Borrower',
      value: '',
      required: true,
    },
    minCreditScore: {
      inputType: 'text',
      type: 'number',
      title: 'Min Credit Score',
      value: '',
      required: true,
    },
    maxMultipleProperties: {
      inputType: 'text',
      type: 'number',
      title: 'Max Additional Properties',
      value: '',
      required: true,
    },
    commitmentFeeRequiredTypes: {
      inputType: 'text',
      type: 'text',
      title: 'Min/Max Commitment Fee Amount',
      value: '',
      readOnly: true,
    },
    dueDilligenceRequiredTypes: {
      inputType: 'text',
      type: 'text',
      title: 'Min/Max Due Dilligence Amount',
      value: '',
      readOnly: true,
    },
    minBrokerFeePercent: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Min Broker Fee Percent',
      value: '',
      required: true,
    },
    maxBrokerFeePercent: {
      inputType: 'text',
      type: 'number',
      title: 'Max Broker Fee Percent',
      value: '',
      prefix: '%',
      required: true,
    },
    minBrokerFeeAmount: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Min Broker Fee Amount',
      value: '',
      prefix: '$',
      required: true,
    },
    defaultUnderwritingFee: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Default Underwriting Fee',
      value: '',
      prefix: '$',
      required: true,
    },
    rateSheetTime: {
      inputType: 'text',
      type: 'number',
      title: 'Rate Sheet Time',
      value: '',
      prefix: 's',
      required: true,
    },
    priceDisplay: {
      inputType: 'select',
      title: 'Price Display',
      options: ['Price', 'Points'],
      hasDefaultOption: true,
      required: true,
    },
    defaultChannel: {
      inputType: 'select',
      title: 'Default Channel',
      options: getLoanTypeMapForSelect(),
      required: true,
    },
    leverageVisible: {
      inputType: 'multiselect',
      title: 'Visible Leverage',
      options: LeverageVisibleKeyOptions,
      defaultSelected: false,
      value: {},
      required: true,
    },
    showPricingForBorrower: {
      inputType: 'check',
      title: 'Show Pricing for Borrower',
      value: false,
    },
    appraisalSection: {
      inputType: 'section',
      title: 'Appraisal',
      span: 'full',
    },
    appraisalStatisticsVisible: {
      inputType: 'multiselect',
      title: 'Visible Appraisal Status',
      options: Appraisal2StatusType,
      defaultSelected: false,
      value: {},
      required: true,
    },
    pipelineSection: {
      inputType: 'section',
      title: 'Pipeline',
      span: 'full',
    },
    useSavedPipelineFilter: {
      inputType: 'check',
      title: 'Use Saved Pipeline Filter',
      value: true,
    },
    // Inventory records are properties held for sale rather than deals. Off by default: with it off the
    // Inventory checkbox on Loan Overview and the pipeline's Inventory filter are both hidden, so a
    // customer that does not hold inventory never sees the feature.
    inventoryEnabled: {
      inputType: 'check',
      title: 'Enable Inventory',
      value: false,
    },
    potentialDuplicateSection: {
      inputType: 'section',
      title: 'Potential Duplicate',
      span: 'full',
    },
    potentialDuplicateHeaders: {
      inputType: 'custom',
      title: 'Potential Duplicate Headers',
      span: 'full',
      value: [],
      render: () => null,
    },
  }
}

export const defaultInputsV2 = (): Record<string, InputType> => {
  const accountTypeWithBorrower = _accountTypeWithBorrower()

  return {
    overviewSection: {
      inputType: 'section',
      title: 'Loan Overview Banner',
      span: 'full',
    },
    overviewCopyTemplate: {
      inputType: 'custom',
      title: 'Overview Copy / Notes Subject Template',
      span: 'full',
      value: '{LoanNumber} | {EntityName} | {BorrowerName} | {SubjectPropertyAddress}',
      render: () => null,
    },
    overviewFieldsOrder: {
      inputType: 'custom',
      title: 'Field Order',
      span: 'full',
      render: () => {},
    },
    overviewFieldsEnabled: {
      inputType: 'custom',
      title: '',
      span: 'full',
      value: {},
      render: () => null,
    },
    overviewFieldsEditable: {
      inputType: 'custom',
      title: '',
      span: 'full',
      value: {},
      render: () => null,
    },
    loanNumber: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Loan Number',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    entityName: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Entity Name',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    borrowerName: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Borrower Name',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    propertyAddress: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Property Address',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    loanProgram: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Loan Program',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    preferredLanguage: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Preferred Language',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    mers: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'MERS',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    closingDate: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Closing Date',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    // Read-only: these three are the servicing figures the loan already carries, so the Editable
    // toggle has no effect on them. They draw only on a loan that is being serviced, and only on
    // the Loan Overview screen itself.
    maturityDate: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Maturity Date',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    nextPaymentDate: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Next Payment Due',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    daysLate: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Days Late',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    thirdPartyId: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'ThirdParty ID',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    coBorrowerGuarantor: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Co-Borrower / Guarantor Contacts',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },

    productSection: {
      inputType: 'section',
      title: 'Pricing Product Display',
      span: 'full',
    },
    productType: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Type',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productProgram: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Program',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productRate: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Rate',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productPrice: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Price / Points',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productRateDate: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Rate Date',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productLockDays: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Lock Days / Expiration Date',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productRateSpread: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Rate Spread',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    productExitFee: {
      inputType: 'multiselect',
      options: accountTypeWithBorrower,
      title: 'Exit Fee',
      sort: true,
      sortKey: 'value',
      defaultSelected: false,
    },
    mfaEnabled: {
      inputType: 'togglebutton',
      title: 'Enable Multi-factor Authentication',
    },
    jwtTokenExpiresIn: {
      inputType: 'text',
      type: 'number',
      title: 'Site Login Time Out in (Days)',
      required: true,
    },
    warningPwChangeInValue: {
      inputType: 'text',
      type: 'number',
      title: 'Warning Password Changes in (Days)',
      required: true,
    },
    requirePwChangeInValue: {
      inputType: 'text',
      type: 'number',
      title: 'Require Password Changes in (Days)',
      required: true,
    },
    documentSection: {
      inputType: 'section',
      title: 'Document Settings',
      span: 'full',
    },
    excelPassword: {
      inputType: 'text',
      type: 'password',
      title: 'Excel Password',
      required: false,
    },
    partySection: {
      inputType: 'section',
      title: 'Parties',
      span: 'full',
    },
    partyView: {
      inputType: 'multiselect',
      options: {},
      title: 'Profile Party View',
      span: 'full',
      defaultSelected: false,
    },
    partyInfo: {
      inputType: 'select',
      options: { email: 'Email', name: 'Name', companyName: 'Company Name' },
      title: 'Party Information Display',
    },
    pullThroughRatioSection: {
      inputType: 'section',
      title: 'Pull Through Ratio',
      span: 'full',
    },
    pullThroughRatioView: {
      inputType: 'multiselect',
      options: pullThroughRatioPartiesMap,
      title: 'Pull Through Ratio Party View',
      defaultSelected: false,
    },
    hidePullThroughRatio: {
      inputType: 'togglebutton',
      title: 'Hide Pull Through Ratio',
    },
    pullThroughRatioKeys: {
      inputType: 'custom',
      title: 'Pull Through Ratio Party Fields',
      span: 'full',
      render: () => {},
    },
    pullThroughRatioScreens: {
      inputType: 'custom',
      title: 'Show on Screens',
      span: 'full',
      value: {},
      render: () => {},
    },
    channelOptionsSection: {
      inputType: 'section',
      title: 'Channel Options',
      span: 'full',
    },
    channelOptions: {
      inputType: 'custom',
      title: 'Channel Options',
      span: 'full',
      value: defaultChannelOptions(),
      render: () => {},
    },
    lei: {
      inputType: 'text',
      title: 'Legal Entity Identifier',
    },
    mersId: {
      inputType: 'text',
      title: 'MERS ID',
    },
    pipelineSection: {
      inputType: 'section',
      title: 'Pipeline',
      span: 'full',
    },
    defaultPipeline: {
      inputType: 'select',
      options: ['Default', 'Servicing', 'FCI'],
      title: 'Default Pipeline',
    },
    notificationsSection: {
      inputType: 'section',
      title: 'Notifications',
      span: 'full',
    },
    notificationsEnabled: {
      inputType: 'togglebutton',
      title: 'Enable Notifications',
      tooltip: 'Shows the notification bell in the header. While off, no notifications are recorded.',
    },
    servicingMenuSection: {
      inputType: 'section',
      title: 'Servicing',
      span: 'full',
    },
    servicingMenuOrder: {
      inputType: 'custom',
      title: 'Servicing Menu',
      span: 'full',
      render: () => {},
    },
    // Rendered by the Servicing Menu list above rather than as a row of its own, but still an
    // input so that it is saved with everything else.
    servicingMenuVisible: {
      inputType: 'custom',
      title: '',
      span: 'full',
      value: {},
      render: () => null,
    },
    eSignSection: {
      inputType: 'section',
      title: 'E-Sign',
      span: 'full',
    },
    eSignBrand: {
      inputType: 'text',
      title: 'E-Sign Certificate Brand',
    },
    regionalSection: {
      inputType: 'section',
      title: 'Regional',
      span: 'full',
    },
    defaultTimezone: {
      inputType: 'select',
      title: 'Default Timezone',
      options: timeZoneMap,
      hasDefaultOption: true,
      defaultOptionText: 'America, New York (default)',
      tooltip: 'Timezone timestamps render in for users who have not picked one in Profile > Configurations.',
    },
  }
}

export const pipelineViewKeys: Record<string, Record<string, string>> = {
  Default: {
    no: 'Loan #',
    borrower: 'Borrower / Property Address',
    purpose: 'Purpose / Product',
    loanStatus: 'Status',
    asOf: 'As Of',
    noteDate: 'Notes Date',
    proposedLoanAmount: 'Loan Amount',
    lockExp: 'Lock Exp',
    party: 'Account Executive / Broker / Creator',
    underwriterName: 'U.W',
    type: 'Channel',
  },
  Servicing: {
    no: 'Loan #',
    borrower: 'Borrower / Property Address',
    purpose: 'Purpose / Product',
    loanStatus: 'Status',
    asOf: 'As Of',
    noteDate: 'Notes Date',
    proposedLoanAmount: 'Loan Amount',
    // What the loan owes today, as against `Loan Amount` beside it, which is what it was written
    // for. `Current Balance` on the FCI pipeline is that feed's own copy and stays empty on a book
    // this platform services itself, so the servicing pipeline needs its own column.
    upb: 'UPB',
    investorCode: 'Investor',
    lastDrawIssueDate: 'Last Draw Issue Date',
    maturityDate: 'Maturity Date',
    type: 'Channel',
  },
  FCI: {
    no: 'Loan #',
    borrower: 'Borrower / Property Address',
    purpose: 'Purpose / Product',
    loanStatus: 'Status',
    originalBalance: 'Original Balance',
    currentBalance: 'Current Balance',
    daysLate: 'Days Late',
    nextDueDate: 'Next Due Date',
    maturityDate: 'Maturity Date',
    noteRate: 'Note Rate',
    totalPayment: 'Total Payment',
    defaultInterestRate: 'Default Interest Rate',
    originationDate: 'Origination Date',
  },
}

export const uiInputs = (): Record<string, InputType> => {
  return {
    primary: {
      inputType: 'text',
      type: 'color',
      title: 'Primary Color',
      required: true,
    },
    secondary: {
      inputType: 'text',
      type: 'color',
      title: 'Secondary Color',
      required: true,
    },
    siteStyle: {
      inputType: 'select',
      title: 'Site Style',
      hasDefaultOption: false,
      options: USER_STYLE_LABELS,
      value: 'modern',
      tooltip: 'Used when a user has not chosen a Site Style in Profile > Configurations.',
    },
    publicPageTheme: {
      inputType: 'select',
      title: 'Landing Page Theme',
      hasDefaultOption: false,
      options: { default: 'Default', theme1: 'Theme1', theme2: 'Theme2', theme3: 'Theme3' },
      value: 'default',
    },
  }
}
